import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpEvent } from '@angular/common/http';
import { firstValueFrom, Observable, Subject, takeUntil } from 'rxjs';
import { Response } from '../../../../_shared/models';
import { Pagination } from '../../../../_shared/models/pagination-response.model';
import { AccidentAlert, DetectedAccident, IncidentReport } from '../models/accidents-and-incidents-response.model';
import {
  AccidentsAndIncidentsRequest,
  IncidentExportFormat,
  IncidentExportRequest,
  UpsertIncidentRequest
} from '../models/accidents-and-incidents-request.model';
import { environment } from '../../../../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class AccidentsAndIncidentsService {
  private httpClient: HttpClient = inject(HttpClient);

  private readonly baseUrl = `${environment.apiEndpoint}/AccidentsAndIncidents`;

  private incidentsChanged = new Subject<void>();

  // Fires after an incident is created or edited, so open incident tables can reload
  incidentsChanged$ = this.incidentsChanged.asObservable();

  async getAccidentAlerts(request: AccidentsAndIncidentsRequest, cancellation: Subject<void>): Promise<Response<Pagination<AccidentAlert[]>>> {
    return firstValueFrom(
      this.httpClient.post<Response<Pagination<AccidentAlert[]>>>(
        `${this.baseUrl}/accidents-alerts/filter`,
        request
      ).pipe(takeUntil(cancellation))
    );
  }

  async getDetectedAccidents(request: AccidentsAndIncidentsRequest, cancellation: Subject<void>): Promise<Response<Pagination<DetectedAccident[]>>> {
    return firstValueFrom(
      this.httpClient.post<Response<Pagination<DetectedAccident[]>>>(
        `${this.baseUrl}/detected-alerts/filter`,
        request
      ).pipe(takeUntil(cancellation))
    );
  }

  async getIncidentReports(request: AccidentsAndIncidentsRequest, cancellation: Subject<void>): Promise<Response<Pagination<IncidentReport[]>>> {
    return firstValueFrom(
      this.httpClient.post<Response<Pagination<IncidentReport[]>>>(
        `${this.baseUrl}/incident-details`,
        request
      ).pipe(takeUntil(cancellation))
    );
  }

  async exportIncidentReports(request: IncidentExportRequest, format: IncidentExportFormat, cancellation: Subject<void>): Promise<Blob> {
    return firstValueFrom(
      this.httpClient.post(
        `${this.baseUrl}/incident-details/export/${format}`,
        request,
        {
          responseType: 'blob'
        }
      ).pipe(takeUntil(cancellation))
    );
  }

  async upsertIncident(request: UpsertIncidentRequest, cancellation: Subject<void>): Promise<Response<void>> {
    const response = await firstValueFrom(
      this.httpClient.post<Response<void>>(
        `${this.baseUrl}/incidents/upsert`,
        request
      ).pipe(takeUntil(cancellation))
    );

    this.incidentsChanged.next();
    return response;
  }

  uploadTempIncidentAttachment(request: FormData, cancellation: Subject<void>): Observable<HttpEvent<Response<void>>> {
    return this.httpClient.post<Response<void>>(
      `${this.baseUrl}/incidents/attachments/temp`,
      request,
      {
        reportProgress: true,
        observe: 'events'
      }
    ).pipe(
      takeUntil(cancellation)
    );
  }
}
